import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Switch } from '@components/Switch';
import { Skeleton } from '@mui/material';
import TeamSelect from '@features/TeamSelect';
import ChannelLocationSelect from '@features/ChannelLocationSelect';
import { useTranslation } from '@hooks';
import { channelsService } from '@services';
import { CHANNEL } from '@constants/channels';

const Container = styled('div')({
    display: 'grid',
    gridGap: '16px 24px',
    gridTemplateColumns: '100%',
    '@media (max-width: 600px)': {
        gridGap: '16px',
    },
});

const FacebookForm = ({ type, isEdit, selectedChannel }) => {
    const { t } = useTranslation();
    const { control, setValue } = useFormContext();

    const [pages, setPages] = useState([]);
    const [pagesLoading, setPagesLoading] = useState(!isEdit);

    const isInstagramModal = type === CHANNEL.INSTAGRAM;

    useEffect(() => {
        if (isEdit) return;
        const getPages = isInstagramModal ? channelsService.getInstagramPages : channelsService.getFacebookPages;
        getPages()
            .then(setPages)
            .catch(() => setPages([]))
            .finally(() => setPagesLoading(false));
    }, [isEdit, isInstagramModal]);

    // The same page can be reachable through several integrations, so options are
    // keyed by page + integration rather than page alone.
    const pageKey = page => `${page.pageId}::${page.integrationId || ''}`;

    const pageOptions = useMemo(
        () => pages.map(page => ({ value: pageKey(page), label: page.name || page.pageId })),
        [pages],
    );

    return (
        <>
            {!pagesLoading && (
                <Container>
                    <Controller
                        control={control}
                        name="name"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsChannels.channelName')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                    {!isEdit && (
                        <Controller
                            control={control}
                            name="pageKey"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Select
                                    {...field}
                                    ref={ref}
                                    onChange={({ target: { value } }) => {
                                        const page = pages.find(p => pageKey(p) === value);
                                        setValue('pageKey', value, { shouldValidate: true, shouldDirty: true });
                                        setValue('pageId', page?.pageId || '', { shouldDirty: true });
                                        setValue('integrationId', page?.integrationId || null, { shouldDirty: true });
                                    }}
                                    options={pageOptions}
                                    required
                                    label={t('SettingsChannels.facebookPage')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                />
                            )}
                        />
                    )}
                    <TeamSelect data={selectedChannel} />
                    {isInstagramModal && (
                        <Controller
                            control={control}
                            name="saveInstagramReplies"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={field.value}
                                    handleChange={event => {
                                        setValue('saveInstagramReplies', event.target.checked, {
                                            shouldDirty: true,
                                        });
                                    }}
                                    label={t('SettingsChannels.saveInstagramReplies')}
                                />
                            )}
                        />
                    )}
                    <ChannelLocationSelect data={selectedChannel} />
                </Container>
            )}
            {pagesLoading && (
                <div style={{ display: 'grid', rowGap: '32px' }}>
                    <Skeleton variant="rounded" width="90%" height={40} />
                    <Skeleton variant="rounded" width="90%" height={40} />
                    <Skeleton variant="rounded" width="50%" height={40} />
                </div>
            )}
        </>
    );
};

export default FacebookForm;
